import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const RegionalSubsidiesSection = () => {
  const [selectedProvince, setSelectedProvince] = useState('all');

  const provinces = [
    { id: 'all', name: 'Heel Nederland' },
    { id: 'noord-holland', name: 'Noord-Holland' },
    { id: 'zuid-holland', name: 'Zuid-Holland' },
    { id: 'utrecht', name: 'Utrecht' },
    { id: 'gelderland', name: 'Gelderland' },
    { id: 'noord-brabant', name: 'Noord-Brabant' }
  ];

  const regionalSubsidies = [
    {
      province: 'noord-holland',
      city: 'Amsterdam',
      subsidy: 'Amsterdamse Energietransitie',
      amount: '€2.500',
      description: 'Extra subsidie voor isolatie en warmtepompen',
      icon: 'Home'
    },
    {
      province: 'zuid-holland',
      city: 'Rotterdam',
      subsidy: 'Rotterdam Klimaatakkoord',
      amount: '€3.000',
      description: 'Subsidie voor verduurzaming woningen',
      icon: 'Leaf'
    },
    {
      province: 'utrecht',
      city: 'Utrecht',
      subsidy: 'Utrecht Duurzaam',
      amount: '€2.000',
      description: 'Subsidie voor zonnepanelen en isolatie',
      icon: 'Sun'
    },
    {
      province: 'gelderland',
      city: 'Arnhem',
      subsidy: 'Arnhem Energieneutraal',
      amount: '€1.500',
      description: 'Subsidie voor energiebesparende maatregelen',
      icon: 'Zap'
    },
    {
      province: 'noord-brabant',
      city: 'Eindhoven',
      subsidy: 'Eindhoven Klimaatneutraal',
      amount: '€2.200',
      description: 'Subsidie voor warmtepompen en isolatie',
      icon: 'Thermometer'
    },
    {
      province: 'noord-holland',
      city: 'Haarlem',
      subsidy: 'Haarlem Verduurzaamt',
      amount: '€1.800',
      description: 'Subsidie voor duurzame energie',
      icon: 'Battery'
    }
  ];

  const filteredSubsidies = selectedProvince === 'all'
    ? regionalSubsidies
    : regionalSubsidies?.filter(s => s?.province === selectedProvince);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Regionale subsidies
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Ontdek welke extra subsidies uw gemeente of provincie biedt
          </p>
        </motion.div>

        {/* Province Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {provinces?.map((province) => (
            <motion.button
              key={province?.id}
              onClick={() => setSelectedProvince(province?.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                selectedProvince === province?.id
                  ? 'bg-primary text-white shadow-elevation-2'
                  : 'bg-white text-muted-foreground hover:bg-gray-100 shadow-elevation-1'
              }`}
            >
              {province?.name}
            </motion.button>
          ))}
        </motion.div>

        {/* Subsidies Grid */}
        <motion.div
          key={selectedProvince}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredSubsidies?.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.03, y: -5 }}
              className="bg-white rounded-xl p-6 shadow-elevation-2 border border-gray-200"
            >
              {/* Icon */}
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Icon name={item?.icon} size={24} className="text-primary" />
              </div>

              {/* City */}
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span className="text-sm font-semibold text-muted-foreground">{item?.city}</span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-foreground mb-3">{item?.subsidy}</h3>

              {/* Amount */}
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Euro" size={16} className="text-success" />
                <p className="text-2xl font-bold text-success">{item?.amount}</p>
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4">{item?.description}</p>

              {/* CTA */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold text-sm hover:bg-primary/20 transition-colors"
              >
                <span>Meer informatie</span>
                <Icon name="ArrowRight" size={14} />
              </motion.button>
            </motion.div>
          ))}
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-4xl mx-auto"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Icon name="Info" size={24} className="text-blue-600" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold text-foreground mb-2">
                Combineer landelijke en lokale subsidies
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Veel gemeentelijke subsidies zijn combineerbaar met landelijke regelingen zoals ISDE en SEEH. 
                Hierdoor kunt u tot 40-50% van uw investering terugkrijgen. Check altijd de voorwaarden 
                en aanvraagprocedures bij uw gemeente.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RegionalSubsidiesSection;